import { Injectable, Injector } from '@angular/core';
import { HttpDataService } from './http-data.service';
import { CataloguesService } from './catalogues.service';
import { Course } from 'core/models/course';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { lastValueFrom } from 'rxjs';

const { oldGamesCanonicNames } = environment;

@Injectable()
export class CoursesService extends HttpDataService {
  private catalogue: any;
  private cachedClientId: string;
  private gamesColumns: any;

  constructor(
    protected injector: Injector,
    private cataloguesService: CataloguesService,
    private translate: TranslateService
  ) {
    super(injector, '/courses');
  }

  loadAccountCoursesCache(meta = false, refreshCache = false) {
    if (!refreshCache && this.catalogue && this.cachedClientId == null) {
      return Promise.resolve(this.catalogue);
    }
    this.cachedClientId = null;
    const archived = true;
    // Account courses are served by the back mimicking a catalogue structure for practical reasons
    return this.getAccountCourses(meta, archived).then((accountCourses) => (this.catalogue = accountCourses));
  }

  loadClientCatalogueCache(clientId: string, meta = false, refreshCache = false) {
    if (
      !refreshCache &&
      this.catalogue &&
      this.cachedClientId &&
      this.cachedClientId === clientId &&
      this.catalogue.clientId === clientId
    ) {
      return Promise.resolve(this.catalogue);
    }
    this.cachedClientId = clientId;
    const archived = true;
    return this.getClientCatalogue(clientId, meta, archived).then((catalogue) => (this.catalogue = catalogue));
  }

  getClientCatalogue(clientId: string, meta = false, archived = false) {
    const lang = localStorage.getItem('backofficelanguage') || 'es';
    const metaQuery = meta ? `&meta=true&lang=${lang}` : '';
    const archivedQuery = archived ? `&archived=true` : '';
    const itineraries = '&itineraries=true';
    const query = `?${metaQuery}${archivedQuery}${itineraries}`;
    const url = `${this.baseUrl}/catalogues/clients/${clientId}${query}`;

    return lastValueFrom(this.http.get(url))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  async getClientCourses(clientId: string, meta = false) {
    if (
      this.catalogue &&
      this.cachedClientId &&
      this.cachedClientId === clientId &&
      this.catalogue.clientId === clientId
    ) {
      return Promise.resolve(this.catalogue);
    }
    return await this.getClientCatalogue(clientId, meta);
  }

  loadGamesColumnsCache() {
    if (this.gamesColumns) {
      return Promise.resolve(this.gamesColumns);
    }
    return this.getGamesColumns().then((gamesColumns) => (this.gamesColumns = gamesColumns));
  }

  getCatalogue() {
    return this.catalogue;
  }

  getCourses() {
    return this.catalogue;
  }

  getCourse(clientId: string, courseId: string) {
    return clientId && this.cachedClientId === clientId
      ? this.catalogue.courses.find((course) => course.id === courseId)
      : null;
  }

  getCachedCourseData(courseId: string) {
    let course = null;
    if (this.catalogue && this.catalogue.courses) {
      const cachedCourse =
        this.catalogue.courses.find((course) => course.id === courseId) ||
        this.catalogue.itineraries.find((course) => course.id === courseId);
      const { catalogue = {}, catalogueItem = { accessMode: 'default' }, ...courseData } = cachedCourse;
      const accessMode = catalogueItem.accessMode || catalogue.defaultAccessMode || courseData.accessMode;
      const configuration = catalogueItem.configuration || catalogue.configuration || courseData.configuration;
      course = {
        ...courseData,
        accessMode,
        configuration
      };
    }
    return course;
  }

  getOptions(onlyActiveCourses = false) {
    const courses = this.getCourses();
    return courses
      ? courses.courses
          .filter((course) => !this._isMarsCourse(course))
          .filter((course) => (onlyActiveCourses ? !course.withdrawn && !course.archived : true))
          .map((course) => this._mapCourseOption(course))
      : [];
  }

  getCoursesItinerariesOptions(onlyActiveCourses = false) {
    const catalog = this.getCourses();
    const { courses, itineraries } = catalog;
    const coursesGroup = courses
      .filter((course) => !this._isMarsCourse(course))
      .filter((course) => (onlyActiveCourses ? !course.withdrawn && !course.archived && course.publish.active : true))
      .map((course) => this._mapCourseOption(course));

    const itinerariesGroup = itineraries
      .filter((itinerary) =>
        onlyActiveCourses ? !itinerary.withdrawn && !itinerary.archived && itinerary.publish.active : true
      )
      .map((itinerary) => ({
        key: itinerary.id,
        value: itinerary.title || itinerary.name,
        data: itinerary
      }));
    return [
      { section: this.translate.instant('properties.courses'), options: coursesGroup },
      { section: this.translate.instant('properties.itineraries'), options: itinerariesGroup }
    ];
  }

  getMarsCourse() {
    const courses = this.getCourses();
    const mars = courses
      ? courses.courses.filter((course) => course.type === 'game' && course.gameCanonicName === 'mars')
      : [];
    return mars.length ? mars[0] : null;
  }

  hasMarsEnabled() {
    if (this.cachedClientId) {
      const courses = this.getCourses();
      return courses
        ? courses.courses.some((course) => this._isMarsCourse(course) && !course.withdrawn && !course.archived)
        : false;
    }
    return false;
  }

  private _mapCourseOption(course) {
    const courseTitle = course.title || course.name;
    return {
      key: course.id,
      value: course.type === 'game' || !course.gameName ? courseTitle : `${courseTitle} (${course.gameName})`,
      data: course
    };
  }

  private _isMarsCourse(course): boolean {
    return course.type === 'mars' || course.gameCanonicName === 'mars';
  }

  getCourseColumns(courseId: string, course = null) {
    if (!course) {
      course = this.getCourses().find((course) => course.id === courseId);
    }
    const gameColumns = course?.gameCanonicName ? this.gamesColumns[course.gameCanonicName] : null;

    return gameColumns
      ? gameColumns
      : [
          {
            name: course.configuration.pointsFieldName || 'points',
            label: 'reports.points',
            sort: true
          },
          {
            name: course.configuration.turnFieldName || 'turn',
            label: 'reports.level'
          }
        ];
  }
  //TODO: triple bypass para recuperar el log de las demos que no tienen información alguna de curso
  getCourseColumnsByGameId(gameId) {
    const course = this.getCourses().find((course) => course.gameId === gameId);
    if (!course) {
      return null;
    }
    const gameColumns = course.gameCanonicName ? this.gamesColumns[course.gameCanonicName] : null;

    return gameColumns
      ? gameColumns
      : [
          {
            name: course.configuration.pointsFieldName || 'points',
            label: 'reports.points',
            sort: true
          },
          {
            name: course.configuration.turnFieldName || 'turn',
            label: 'reports.level'
          }
        ];
  }

  /**
   * Beware: This is a hack for game filter to get games options from courses
   * DO NOT USE ELSEWHERE
   */
  getGamesOptions() {
    const courses = this.getCourses();
    if (!courses) return [];
    const games = courses.courses
      .map((course) => ({
        key: course.gameId,
        value: course.gameName,
        data: course
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
      .filter((game, index, arr) => !index || game.key !== arr[index - 1].key);
    const itineraries = courses.itineraries
      .map((itinerary) => ({
        key: itinerary._id,
        value: itinerary.title || itinerary.name,
        data: itinerary
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
      .filter((game, index, arr) => !index || game.key !== arr[index - 1].key);

    return [
      { section: this.translate.instant('properties.courses'), options: games },
      { section: this.translate.instant('properties.itineraries'), options: itineraries }
    ];
  }

  /*
   * The query param removable has been set to false because it causes a significant delay
   * See https://gamelearn.atlassian.net/browse/LMS-4148
   * When we apply pagination try to set removable to true again
   */
  getAccountCourses(meta = false, archived = false, itineraries = true, removable = false) {
    const lang = localStorage.getItem('backofficelanguage') || 'es';
    const metaQuery = meta ? `&meta=true&lang=${lang}` : '';
    const archivedQuery = archived ? `&archived=true` : '';
    const itineraryQuery = itineraries ? '&itineraries=true' : '';
    const removableQuery = removable ? '&removable=true' : '';
    const query = `?${metaQuery}${archivedQuery}${itineraryQuery}${removableQuery}`;

    return lastValueFrom(this.http.get(`${this.baseUrl}/account${query}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getDemoCourses() {
    return lastValueFrom(this.http.get(`${this.baseUrl}/demo`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getCourseDetail(courseId, clientId = null) {
    const lang = localStorage.getItem('backofficelanguage') || 'es';

    return lastValueFrom(
      this.http.get(`${this.baseUrl}/${courseId}/detail?lang=${lang}${clientId ? `&clientId=${clientId}` : ''}`)
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getCourseMeta(courseId, lang = 'es'): Promise<any> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/meta/${courseId}?lang=${lang}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateCourseMeta(courseId, data) {
    return lastValueFrom(this.http.put(`${this.baseUrl}/meta/${courseId}`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateCoursesBulk(data) {
    return lastValueFrom(this.http.put(`${this.baseUrl}/bulk`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getGamesColumns() {
    return lastValueFrom(this.http.get('public/data/gamesColumns.json'))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  /**
   * Updates a given course.
   * @param courseId              The course's id.
   * @param data                  Course's data to update.
   * @param accessChangeConfirmed Whether course access modification was confirmed.
   * @param lang                  Language in which itineraries affected by course access change
   *                              titles will be returned.
   */
  public async updateCourse(
    courseId: string,
    data: any,
    accessChangeConfirmed: boolean,
    lang: string
  ): Promise<Course | any> {
    return lastValueFrom(
      this.http.put(`${this.baseUrl}/${courseId}?accessChangeConfirmed=${accessChangeConfirmed}&lang=${lang}`, data)
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  /**
   * Download the SCORM zip file of a course/class
   * @param courseId The course's id
   * @param clientId
   * @returns the link of zip file
   */
  public downloadScormCourse(courseId: string, clientId: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/${courseId}/scorm/${clientId}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  public async withdrawCourse(courseId, data): Promise<Course | any> {
    return lastValueFrom(this.http.put(`${this.baseUrl}/${courseId}`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  public async updateCatalogue(catalogueId: string, data: any) {
    const res = await this.cataloguesService.updateCatalogue(catalogueId, data);
    this.catalogue = { ...this.catalogue, ...data };
    return res;
  }

  checkOldGames(group: any): boolean {
    if (group.modules && group.modules.length > 0) {
      return group.modules.some((module) => oldGamesCanonicNames.includes(module.gameCanonicName));
    } else {
      return oldGamesCanonicNames.includes(group.gameCanonicName);
    }
  }

  canModuleGetBonus(course: any): boolean {
    if (course) {
      return oldGamesCanonicNames.every((oldGameCanonicName) => course.gameCanonicName !== oldGameCanonicName);
    }
    return false;
  }

  canModulesGetBonus(itineraryModules: any[]) {
    if (itineraryModules && itineraryModules.length) {
      return itineraryModules.every((module) => this.canModuleGetBonus(module));
    }
    return false;
  }
}
